import React, { useState, useEffect } from 'react';
import { useDataProvider, useNotify, Loading } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert } from '@mui/material';
import { apiClient } from '../../framework/api/api.client';

/**
 * My Tenant - Single tenant view for non-mmadmin environments
 * 
 * Allows tenant instances to manage their own tenant record
 * without seeing a list of all tenants (security isolation)
 */
export const MyTenant = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCurrentTenant();
  }, []);

  const loadCurrentTenant = async () => {
    try {
      // Call backend to get current tenant from authenticated context
      const tenantInfo = await apiClient.call('/api/tenant-info/current');
      
      if (tenantInfo && tenantInfo.tenantId) {
        console.log('✅ Loaded tenant from authenticated context:', tenantInfo);
        setTenantId(tenantInfo.tenantId);
      } else {
        setError('No tenant ID found in authenticated context. Please ensure you are logged in with a tenant-scoped account.');
      }
    } catch (err: any) {
      console.error('Failed to load tenant:', err);
      setError(err.message || 'Failed to load tenant information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error || !tenantId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>Unable to Load Tenant</Typography>
          <Typography variant="body2">
            {error || 'Tenant ID not configured in environment'}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Set VITE_TENANT_ID in your .env file or ensure the backend provision endpoint is configured.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Create a fake "edit" context with the tenant ID
  // This allows TenantEditEnhanced to work without being in a list→edit flow
  return (
    <Box>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Typography variant="h5">Tenant Settings</Typography>
        <Typography variant="body2" color="textSecondary">
          Manage your tenant configuration, brands, locations, and API keys
        </Typography>
      </Box>
      
      {/* Render the tenant edit form directly */}
      <Box sx={{ px: 2 }}>
        <TenantEditComponent tenantId={tenantId} />
      </Box>
    </Box>
  );
};

// Wrapper to provide the tenant record context
const TenantEditComponent = ({ tenantId }: { tenantId: string }) => {
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTenant();
  }, [tenantId]);

  const loadTenant = async () => {
    try {
      // Use getList with filter by id (UUID) instead of getOne which expects ObjectId
      const { data } = await dataProvider.getList('tenant', {
        filter: { id: tenantId },
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'name', order: 'ASC' }
      });
      
      if (data && data.length > 0) {
        setTenant(data[0]);
      } else {
        console.error('Tenant not found with id:', tenantId);
      }
    } catch (error) {
      console.error('Failed to load tenant:', error);
    } finally {
      setLoading(false);
    }
  };

  // Redirect once tenant is loaded
  useEffect(() => {
    if (tenant?.id) {
      navigate(`/tenant/${tenant.id}`);
    }
  }, [tenant]);

  if (loading) {
    return <Loading />;
  }

  if (!tenant) {
    return (
      <Alert severity="error">
        Tenant not found. Please check your configuration.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography>Redirecting to tenant settings...</Typography>
    </Box>
  );
};

export default MyTenant;

